import React, { useEffect, useState } from "react";
import Gantt from "./components/gantt";
import Popup from "../../components/Popup";
import { Box, FormControlLabel, Switch, Grid, Stack } from "@mui/material";
import { FormattedMessage } from "react-intl";
import MachineStopEditDialog from "../machineDetails/components/machineStopEditDialog";
import AxionControls from "../../components/controls/AxionControls";
import { useNavigate } from 'react-router-dom';
import { useQuery } from "@tanstack/react-query";
import { getStopReasonCodes } from "../stopReasonCodes/hooks/fetchers";
import { fetchMachineData, fetchMachineStop } from "../../fetchers/machine";
import { pushErrorMessage } from '../../utils/notifications';
import dayjs from "dayjs";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import DateFilter from "../machineDetails/components/DateFilter";
import { getTimespan } from "../../fetchers/timespan";
import messages from "./messages";
import { alpha, useTheme } from "@mui/material/styles";
import { tokens } from "../../assets/theme";
import { fetchCompany } from "../../fetchers/company";
import { getOpenActivities } from "../activities/hooks/fetchers";
import useFeatureFlags from "../../hooks/useFeatureFlags";
import AlarmBanner from './components/alarmBanner';
import { fetchWeeks } from "../../fetchers/calendar";
import { fetchGanttData } from "../../fetchers/ganttData";
import { fetchAreas } from "../../fetchers/area";
import { fetchOeeDashboardDataTimeSpan } from "../../fetchers/oee";

function convertTZ(date, tzString) {
    return new Date((typeof date === "string" ? new Date(date) : date).toLocaleString("en-US", { timeZone: tzString }));
}

export default function GanttIndex() {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [companyKey, setCompanyKey] = useLocalStorage("companyKey", 1);
    const navigate = useNavigate();

    const [openPopup, setOpenPopup] = useState(false);
    const [machineStop, setMachineStop] = useState(null);
    const [showBackdrop, setShowBackdrop] = useState(false);
    const [filteredStopReasonCodes, setFilteredStopReasonCodes] = useState([]);
    const [filteredGanttData, setFilteredGanttData] = useState();
    const [areaFilter, setAreaFilter] = useLocalStorage("ganttAreaFilter", []);

    const [timeSpan, setTimeSpan] = useLocalStorage("timespan", "currentshift");
    const [startDate, setStartDate] = useLocalStorage("startDate", dayjs());
    const [endDate, setEndDate] = useLocalStorage("endDate", dayjs());
    const [checked, setCheck] = useLocalStorage("checked", {
        running: false,
        onlyPlanned: false,
        onlyStopped: false,
    });
    const [nrOfWeeks, setNrOfWeeks] = useState(52);
    const [selectedWeek, setSelectedWeek] = React.useState(1);
    const featuresQuery = useFeatureFlags();
    
    // We only want to use a custom endDate when viewing a specific week (not "this week"). If we are viewing the other timespans we use the same data and just zoom to the correct date-range in the Gantt component.
    const isHistoricalWeek = timeSpan === "week" && selectedWeek < 0;

    // All dates for the last 10 days share the same data, so we can use a static key there
    const ganttModeKey = isHistoricalWeek
        ? `week-${selectedWeek}`  
        : "current";

    const ganttDataQuery = useQuery(
        ["ganttData", companyKey, ganttModeKey],
        () =>
            fetchGanttData(
                companyKey,
                isHistoricalWeek ? endDate : null   
            ),
        {
            refetchInterval: 300000,
            staleTime: 300000,
            keepPreviousData: true
        }
    );

    const stopReasonCodesQuery = useQuery(
        ["stopReasonCodes", companyKey],
        () => getStopReasonCodes(companyKey),
        {
            refetchInterval: 60000,
            staleTime: 60000,
            keepPreviousData: true
        }
    );

    const DatesForTimespanQuery = useQuery(
        ["datesForTimespan", timeSpan, companyKey],
        () => getTimespan(timeSpan, companyKey),
        {
            refetchInterval: 60000,
            staleTime: 60000,
            keepPreviousData: true
        }
    );

    const companyQuery = useQuery(
        ["company", companyKey],
        () => fetchCompany(companyKey),
        {
            refetchInterval: 300000,
            staleTime: 60000,
            keepPreviousData: true
        }
    );

    const openActivitiesQuery = useQuery(
        ["openActivities", companyKey],
        () => getOpenActivities(companyKey),
        {
            staleTime: 60000,
            refetchInterval: 60000,
            keepPreviousData: true
        }
    );

    const weeksQuery = useQuery(
        ["weeks", nrOfWeeks, companyKey],
        () => fetchWeeks(nrOfWeeks, companyKey),
        {
            staleTime: 60000,
            refetchInterval: 120000,
            keepPreviousData: true
        }
    );

    const areaQuery = useQuery(
        ["areas"],
        () => fetchAreas(),
        {
            keepPreviousData: true
        }
    );

    const oeeData = useQuery(
        ["oeedata", timeSpan, companyKey],
        () => fetchOeeDashboardDataTimeSpan(timeSpan, companyKey),
        {
            staleTime: 300000,
            refetchInterval: 300000,
            enabled: timeSpan !== "week",
            keepPreviousData: true
    });

    useEffect(() => {
        setFilteredStopReasonCodes(stopReasonCodesQuery.data);
    }, [stopReasonCodesQuery.data]);

    useEffect(() => {
        if (DatesForTimespanQuery.isFetched && timeSpan !== 'manual' && !DatesForTimespanQuery.isError) {
            // Temporary fix until we have a generic handling of timezones
            if (companyQuery.data?.timezoneWindows !== companyQuery.data?.machineBaseTimezoneWindows && DatesForTimespanQuery.data[0].fromDate !== null && DatesForTimespanQuery.data[0].toDate !== null && (timeSpan === '24h' || timeSpan === '72h' || timeSpan === 'week')) {
                setStartDate(convertTZ(DatesForTimespanQuery.data[0].fromDate, companyQuery.data?.timezone ?? "America/New_York"));
                setEndDate(convertTZ(DatesForTimespanQuery.data[0].toDate, companyQuery.data?.timezone ?? "America/New_York"));
            }
            else {
                setStartDate(DatesForTimespanQuery.data[0].fromDate);
                setEndDate(DatesForTimespanQuery.data[0].toDate);
            }
        }
    }, [DatesForTimespanQuery.data, DatesForTimespanQuery.isFetched, DatesForTimespanQuery.isError, timeSpan, companyKey]);

    useEffect(() => {
        setFilteredGanttData(ganttDataQuery?.data);
        filterMachines();
    }, [ganttDataQuery.isSuccess, ganttDataQuery.data]);

    useEffect(() => {
        if (filteredGanttData === undefined) { return; }
        filterMachines();
    }, [checked])

    useEffect(() => {
        if (ganttDataQuery?.data === undefined) { return; }
        filterMachines();
    }, [areaFilter])

    const filterMachines = () => {
        if (ganttDataQuery?.data === undefined) { return; }

        let areaFilteredData = [];
        if (areaFilter.length > 0) {
            areaFilteredData = ganttDataQuery?.data.filter(area => areaFilter.some(af => af.id.toString() === area.id.toString()));
        }
        else {
            areaFilteredData = ganttDataQuery?.data;
        }

        if (checked.running) {
            setFilteredGanttData(areaFilteredData.map(area => ({
                ...area,
                children: area.children.filter(machine => machine.isRunning === true)
            })));
        }
        else if (checked.onlyStopped && checked.onlyPlanned === false) {
            setFilteredGanttData(areaFilteredData.map(area => ({
                ...area,
                children: area.children.filter(machine => machine.isRunning === false)
            })));
        }
        else if (checked.onlyStopped && checked.onlyPlanned) {
            setFilteredGanttData(areaFilteredData.map(area => ({
                ...area,
                children: area.children.filter(machine => machine.isRunning === false && machine.isPlannedStop === false)
            })));
        }
        else if (checked.onlyPlanned) {
            setFilteredGanttData(areaFilteredData.map(area => ({
                ...area,
                children: area.children.filter(machine => machine.isPlannedStop === false)
            })));
        }
        else {
            setFilteredGanttData(areaFilteredData);
        }
    };

    const handleAreaFilter = (event, newValue) => {
        setAreaFilter(newValue);
    }

    if (filteredGanttData === undefined || ganttDataQuery.isLoading) {
        return <AxionControls.AxionBackdrop open={true} />
    }

    if (featuresQuery === undefined || featuresQuery.isLoading) {
        return <AxionControls.AxionBackdrop open={true} />
    }

    if (companyQuery === undefined || companyQuery.data === undefined || companyQuery.isLoading) {
        return <AxionControls.AxionBackdrop open={true} />
    }

    if (openActivitiesQuery === undefined || openActivitiesQuery.data === undefined || openActivitiesQuery.isLoading) {
        return <AxionControls.AxionBackdrop open={true} />
    }

    if (weeksQuery === undefined || weeksQuery.data === undefined || weeksQuery.isLoading) {
        return <AxionControls.AxionBackdrop open={true} />
    }
    if (areaQuery === undefined || areaQuery.data === undefined || areaQuery.isLoading) {
        return <AxionControls.AxionBackdrop open={true} />
    }

    const handleClick = (id) => {
        setShowBackdrop(true);
        fetchMachineStop(id).then((machineStopItem) => {
            fetchMachineData(machineStopItem.machineId).then((machine) => {
                setFilteredStopReasonCodes(stopReasonCodesQuery.data.filter(x => machine.machineStopReasonCodes.some(y => x.id === y.stopReasonCodeId)));
                setMachineStop(machineStopItem);
                setOpenPopup(true);
                setShowBackdrop(false);
            }
            ).catch((error) => {
                console.log(error);
                setShowBackdrop(false);
                pushErrorMessage(`Could not get info about the machine connected to the stop.`);
            })
        }).catch((error) => {
            console.log(error);
            setShowBackdrop(false);
            pushErrorMessage(`Could not get info about the stop.`);
        })
    };

    const handleRefresh = () => {
        ganttDataQuery.refetch();
    }

    const handleSplit = (id) => {
        navigate(`/machinestops/${id}/split`);
    }

    const handleTimeSpan = (ts) => {
        if (ts === "week" && timeSpan !== "week") {
            setSelectedWeek(1);
        }
        // Update state or perform any other action with childData
        setTimeSpan(ts);
    };

    const handleSetSelectedWeekStartDate = (startDate) => {
        if (startDate !== null) {
            setStartDate(startDate);
        }
        else if (companyQuery.data?.timezoneWindows !== companyQuery.data?.machineBaseTimezoneWindows && DatesForTimespanQuery.data[0].fromDate !== null && timeSpan === 'week') {
            setStartDate(convertTZ(DatesForTimespanQuery.data[0].fromDate, companyQuery.data?.timezone ?? "America/New_York"));
        }
        else {
            setStartDate(DatesForTimespanQuery.data[0].fromDate);
        }
    };

    const handleSetSelectedWeekEndDate = (endDate) => {
        if (endDate !== null) {
            setEndDate(endDate);
        }
        else if (companyQuery.data?.timezoneWindows !== companyQuery.data?.machineBaseTimezoneWindows && DatesForTimespanQuery.data[0].toDate !== null && timeSpan === 'week') {
            setEndDate(convertTZ(DatesForTimespanQuery.data[0].toDate, companyQuery.data?.timezone ?? "America/New_York"));
        }
        else {
            setEndDate(DatesForTimespanQuery.data[0].toDate);
        }
    };

    const handleCheck = (event) => {
        if (event.target.name === "running") {
            if (!checked.running) {
                setCheck({
                    ...checked,
                    running: true,
                    onlyPlanned: false,
                    onlyStopped: false,
                });
            }
            if (checked.running) {
                setCheck({
                    ...checked,
                    running: false,
                    onlyPlanned: false
                });
            }
        }
        if (event.target.name === "onlyPlanned") {
            if (!checked.onlyPlanned) {
                if (!checked.onlyStopped) {
                    setCheck({
                        ...checked,
                        running: false,
                        onlyPlanned: true,
                        onlyStopped: false,
                    });
                } else {
                    setCheck({
                        ...checked,
                        running: false,
                        onlyPlanned: true,
                        onlyStopped: true,
                    });
                }
            }
            if (checked.onlyPlanned) {
                setCheck({
                    ...checked,
                    onlyPlanned: false
                });
            }
        }
        if (event.target.name === "onlyStopped") {
            if (!checked.onlyStopped) {
                if (!checked.onlyPlanned) {
                    setCheck({
                        ...checked,
                        running: false,
                        onlyPlanned: false,
                        onlyStopped: true,
                    });
                } else {
                    setCheck({
                        ...checked,
                        running: false,
                        onlyPlanned: true,
                        onlyStopped: true,
                    });
                }
            }
            if (checked.onlyStopped) {
                setCheck({
                    ...checked,
                    onlyStopped: false,
                });
            }
        };
    };
    let alarms = openActivitiesQuery.data?.filter((list) => list.type === 2 && list.activityStatusId === 1)
    return <>
        <Box m="20px" height="94vh" sx={{ backgroundColor: colors.primary[700] }}>
            <Box>
                {alarms.length > 0 &&
                    <Box marginBottom="10px"><AlarmBanner alarms={alarms} /></Box>
                }
                <Grid container columnSpacing={2} rowSpacing={0} sx={{ px: { xs: 2, md: 0 }, pt: { xs: 0.5, md: 0 } }} alignItems="center">
                    <Grid item xs={12} md={5} >
                        <Stack
                            direction={{ xs: "column", md: "row" }}
                            width="100%"
                            ml={{ xs: 0, md: 4 }}
                            flexWrap="wrap"
                            gap={{ xs: 1, md: 1 }}
                        >
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={checked.running}
                                        sx={{
                                            "& .MuiSwitch-switchBase.Mui-checked": {
                                                color: "#799c4a !important",
                                                "&:hover": {
                                                    backgroundColor: alpha("#799c4a", theme.palette.action.hoverOpacity),
                                                },
                                            },
                                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                                backgroundColor: "#799c4a !important",
                                            },
                                        }}
                                        onChange={handleCheck}
                                        name="running"
                                    />
                                }
                                label={<FormattedMessage {...messages.onlyRunningFilter} />}
                                sx={{ mr: { xs: 1, md: 5 } }}
                            />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={checked.onlyPlanned}
                                        sx={{
                                            "& .MuiSwitch-switchBase.Mui-checked": {
                                                color: "#799c4a !important",
                                                "&:hover": {
                                                    backgroundColor: alpha("#799c4a", theme.palette.action.hoverOpacity),
                                                },
                                            },
                                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                                backgroundColor: "#799c4a !important",
                                            },
                                        }}
                                        onChange={handleCheck}
                                        name="onlyPlanned"
                                    />
                                }
                                label={<FormattedMessage {...messages.dontShowPlannedStopsFilter} />}
                                sx={{ mr: { xs: 1, md: 5 } }}
                            />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={checked.onlyStopped}
                                        sx={{
                                            "& .MuiSwitch-switchBase.Mui-checked": {
                                                color: "#799c4a !important",
                                                "&:hover": {
                                                    backgroundColor: alpha("#799c4a", theme.palette.action.hoverOpacity),
                                                },
                                            },
                                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                                backgroundColor: "#799c4a !important",
                                            },
                                        }}
                                        onChange={handleCheck}
                                        name="onlyStopped"
                                    />
                                }
                                label={<FormattedMessage {...messages.onlyStoppedFilter} />}
                                sx={{ mr: { xs: 1, md: 5 } }}
                            />
                        </Stack>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <Box sx={{ width: { xs: "100%", md: 400 }, maxWidth: "100%" }}>
                            <AxionControls.AxionAutocomplete
                                name="Area"
                                label={<FormattedMessage {...messages.area} />}
                                multiple
                                defaultValue={areaFilter || ""}
                                value={areaFilter || ""}
                                options={areaQuery.data
                                    .filter(x => x.companyKey === companyKey)
                                    .map(area => ({ id: area.areaKey, title: area.areaName }))}
                                onChange={(e, v) => handleAreaFilter(e, v)}
                                backgroundColor={colors.primary[700]}
                                borderColor={colors.gray[400]}
                                color={colors.gray[200]}
                                sx={{ mt: 1, mb: 1 }}
                                fullWidth
                            />
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Stack
                            direction={{ xs: "column", md: "row" }}
                            width="100%"
                            ml={{ xs: 0, md: 2 }}
                            spacing={{ xs: 1, md: 2 }}
                        >
                            <DateFilter
                                onDataReceived={handleTimeSpan}
                                startDate={startDate}
                                endDate={endDate}
                                hideManual
                                showWeek
                                weeks={weeksQuery?.data}
                                setSelectedWeekStartDate={handleSetSelectedWeekStartDate}
                                setSelectedWeekEndDate={handleSetSelectedWeekEndDate}
                                selectedWeek={selectedWeek}
                                setSelectedWeek={setSelectedWeek}
                            />
                        </Stack>
                    </Grid>
                </Grid>
            </Box>
            <Gantt data={filteredGanttData} handleClick={handleClick} startDate={startDate} endDate={endDate} company={companyQuery?.data} openActivities={openActivitiesQuery?.data} featureFlags={featuresQuery?.data} oeeData={oeeData?.data} />
        </Box>
        <Popup
            openPopup={openPopup}
            setOpenPopup={setOpenPopup}
            maxWidht="sm"
            title={<FormattedMessage {...messages.editStop} />}>
            <MachineStopEditDialog
                stopReasonCodes={filteredStopReasonCodes}
                refresh={handleRefresh}
                machineStop={machineStop}
                handleSplit={handleSplit}
                setOpenPopup={setOpenPopup}
                setShowBackdrop={setShowBackdrop} />
        </Popup>
        <AxionControls.AxionBackdrop open={showBackdrop} />
    </>
}